export interface PlanogramRecord {
  psaRecordIndex: number;
  rawRecord?: string;
  name: string; // The name of the planogram.
  dbKey: number; // The unique identifier for the planogram. "key" in PSA Headers Speadsheet.
  width: number; // The width of the planogram.
  height: number; // The height of the planogram.
  depth: number; // The depth of the planogram.
  color: number; // The color of the planogram.
  changed: number; // Specifies whether the planogram data has changed during the current Space Planning or Floor Planning session.
  backDepth: number; // Depth of the backboard
  drawBack: number; // Indicates whether or not the back is drawn
  baseWidth: number; // The width of the base
  baseHeight: number; // The height of the base
  baseDepth: number; // The depth of the base
  drawBase: number; // Indicates whether or not the base is drawn.
  baseColor: number; // Indicates whether or not the base is drawn.
  drawNotches: number; // Indicates whether or not the notches are drawn on the planogram.
  notchOffset: number; // The distance from the floor from which to start displaying notches.
  notchSpacing: number; // Indicates the vertical spacing of the notches on the notch bars.
  doubleNotches: number; // Indicates whether or not the notches are double.
  notchColor: number; // The color of the notches on the planogram expressed in RGB integer.
  notchMarks: number; // Indicates whether or not the notch bars are to be drawn along the sides of the planogram’s segments.
  drawPegs: number; // Indicates whether or not the pegs are drawn on the pegboard.
  drawPegholes: number; // Indicates whether or not the pegholes are drawn on the pegboard.
  trafficFlow: number; // The merchandise direction on the planogram
  autoCreated: number; // The merchandise direction on the planogram
  shapeId: string; // The shape ID associated with the planogram.
  bitmapId: string; // The image file associated with the planogram.
  merchXMin: number; // Minimum number of horizontal facings of child positions. On the Planogram Properties, this is labeled “Minimum facings”.
  merchXMax: number; // Maximum number of horizontal facings of child positions, includes horizontal caps. On the Planogram Properties, this is labeled “Maximum facings”.
  merchXUprights: number; // The maximum number of normal upright units horizontally, used to specify the target number of upright facings in conjunction with horizontal capping (MerchYCaps). This will be constrained by MerchXMax or the available merchandising space of the fixture.
  merchXCaps: number; // Maximum vertical number of caps, will be constrained by MerchYMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps High”.
  merchXPlacement: number; // Specifies how the positions are to be placed horizontally. On the Planogram Properties, this is labeled “Placement left/right” (options are Manually placed, At the edge, Stacked against others, Spread evenly).
  merchXNumber: number; // Specifies how the number of horizontal units of child positions is controlled. On the Planogram Properties, this is labeled “Number of facings wide” (options are Manually determined, Only 1, Fill available space).
  merchXSize: number; // Specifies how the number of horizontal units of child positions is controlled. On the Planogram Properties, this is labeled “Number of facings wide” (options are Manually determined, Only 1, Fill available space).
  merchXDirection: number; // Specifies whether or not to base merchandising from right instead of left. On the Planogram Properties, this is labeled “Starting from”.
  merchXSqueeze: number; // Determines whether or not individual facing can be squeezed horizontal. On the Planogram Properties, this is labeled “Facings can be squeezed”.
  merchYMin: number; // Minimum number of vertical facings of child positions. On the Planogram Properties, this is labeled “Minimum units high”.
  merchYMax: number; // Maximum number of vertical facings of child positions, includes vertical caps. On the Planogram Properties, this is labeled “Maximum units high”.
  merchYUprights: number; // Maximum number of vertical facings of child positions, includes vertical caps. On the Planogram Properties, this is labeled “Maximum units high”.
  merchYCaps: number; // Maximum vertical number of caps, will be constrained by MerchYMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps High”.
  merchYPlacement: number; // Maximum vertical number of caps, will be constrained by MerchYMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps High”.
  merchYNumber: number; // Specifies how the number of vertical units of child positions is controlled. On the Planogram Properties, this is labeled “Number of units high” (options are Manually determined, Only 1, Fill available space).
  merchYSize: number; // Indicates how the height of the units within a position is to be controlled. On the Planogram Properties, this is labeled “Height of units” (options are Normal, Expand/Squeeze facings to fill space, Space out facings to fill space).
  merchYDirection: number; // Specifies whether or not to base merchandising from top instead of base. On the Planogram Properties, this is labeled “Starting from”.
  merchYSqueeze: number; // Determines whether or not individual facing can be squeezed vertically. On the Planogram Properties, this is labeled “Facings can be squeezed”.
  merchZMin: number; // Minimum number of units deep of child positions. On the Planogram Properties, this is labeled “Minimum units deep”.
  merchZMax: number; // Maximum number of units deep of child positions, includes depth caps. On the Planogram Properties, this is labeled “Maximum units deep”.
  merchZUprights: number; // Maximum number of units deep of child positions, includes depth caps. On the Planogram Properties, this is labeled “Maximum units deep”.
  merchZCaps: number; // Maximum number of caps deep, will be constrained by MerchZMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps Deep”.
  merchZPlacement: number; // Specifies how the positions are to be placed depth wise. On the Planogram Properties, this is labeled “Placement back/front” (options are Manually placed, At the edge, Stacked against others, Spread evenly).
  merchZNumber: number; // Specifies how the number of units deep of child positions is controlled. On the Planogram Properties, this is labeled “Number of units deep” (options are Manually determined, Only 1, Fill available space).
  merchZSize: number; // Indicates how the depth of the units within a position is to be controlled. On the Planogram Properties, this is labeled “Depth of units” (options are Normal, Expand/Squeeze facings to fill space, Space out facings to fill space).
  merchZDirection: number; // Specifies whether or not to base merchandising from back instead of front. On the Planogram Properties, this is labeled “Starting from”.
  merchZSqueeze: number; // Determines whether or not individual facing can be squeezed depth wise. On the Planogram Properties, this is labeled “Facings can be squeezed”.
  numberOfFixtures: number; // The number of fixtures contained within the planogram
  numberOfSegments: number; // The number of fixtures contained within the planogram
  numberOfDrawings: number; // The number of segments contained within the planogram
  combinedPerformanceIndex: number; // The number of drawn objects on the planogram view
  numberOfStores: number; // The number of stores that are represented by the planogram.
  sales: number; // Total sales for the planogram
  cost: number; // The total cost for all positions on the planogram.
  margin: number; // The total margin for the planogram.
  capacityCost: number; // The total capacity cost for all the positions on the planogram.
  capacityRetail: number; // The total capacity retail for all the positions on the planogram.
  annualProfit: number; // Annual profit for the planogram.
  roiiCost: number; // The ROII cost for the planogram.
  roiiRetail: number; // The ROII retail for the planogram.
  notchWidth: number; // The width of the notch bar.
  desc1: string; // ProtectionDesc: Updated PMD to say "ProtectionDesc" instead of "PlanogramType" 2021.  This field is used to identify the store's ProtectionDesc and should be updating from the store table.
  desc2: string; // Group/Base: Field updated by Enforcer.
  desc3: string; // City: Field updated by Enforcer.
  desc4: string; // City Store #: Field updated by Enforcer.
  desc5: string; // Dept #: Field updated by Enforcer.
  desc6: string; // Dept Name: Field updated by Enforcer.
  desc7: string; // Sub Dept #: Field updated by Enforcer.
  desc8: string; // Sub Dept Name: Field updated by Enforcer.
  desc9: string; // Section: Field updated by Enforcer.
  desc10: string; // Section Size: Field updated by Enforcer.
  desc11: string; // Cluster #: Field updated by Enforcer.
  desc12: string; // Store Cluster: Field updated by Enforcer. Not being updated?  Fresh using 2021 (segmentation)
  desc13: string; // Primary Section Cluster: Field updated by Enforcer.
  desc14: string; // Primary Section
  desc15: string; // POG Type: Field updated by Enforcer.
  desc16: string; // Base/Group POG Name: Field updated by Enforcer.
  desc17: string; // Obstruction: Field updated by Enforcer.
  desc18: string; // Base Shelf Depth
  desc19: string; // Upper Shelf Depth
  desc20: string;
  desc21: string;
  desc22: string; // Profile Type: Values will be high (>61) or low (<61)
  desc23: string; // Other: Field updated by Enforcer.
  desc24: string; // Revision Type: Field updated by Enforcer.
  desc25: string; // Tailored Plan
  desc26: string; // BDM: Field updated in CAS_SPC_PLAN_UPD_PKG for POGs in status 1,2,3,4.  BDM is pulled from cas.heb_us_section.
  desc27: string; // Region: Field updated by Enforcer?  Not being updated. Fresh using 2021.
  desc28: string; // District: Fresh using 2021 (distribution limitations)
  desc29: string; // Keywords: Field updated by Enforcer.
  desc30: string; // Creator
  desc31: string; // Unique
  desc32: string; // Cluster Desc: Fresh using 2021 (assortment attributes)
  desc33: string; // Fixture Type: Fresh using 2021 for case type.
  desc34: string; // Tailored Date
  desc35: string; // Beer Date Info
  desc36: string;
  desc37: string;
  desc38: string;
  desc39: string;
  desc40: string;
  desc41: string;
  desc42: string;
  desc43: string;
  desc44: string;
  desc45: string; // CKS User
  desc46: string;
  desc47: string; // MM
  desc48: string; // Store Assign
  desc49: string; // Store Assign(Cont)
  desc50: string; // All POGs
  value1: number; // ProtectionOrder
  value2: number;
  value3: number;
  value4: number;
  value5: number;
  value6: number;
  value7: number;
  value8: number;
  value9: number;
  value10: number;
  value11: number;
  value12: number;
  value13: number;
  value14: number;
  value15: number;
  value16: number;
  value17: number;
  value18: number;
  value19: number;
  value20: number;
  value21: number;
  value22: number;
  value23: number;
  value24: number;
  value25: number;
  value26: number;
  value27: number;
  value28: number;
  value29: number;
  value30: number; // Archive: If value = 9999 the pog will be deleted from ikb
  value31: number;
  value32: number;
  value33: number;
  value34: number;
  value35: number; // Shelf Life
  value36: number;
  value37: number;
  value38: number;
  value39: number;
  value40: number; // Value = 9999 the pog was created by Create Versioning Proscript; Value = 9998 the pog was created by RG1
  value41: number;
  value42: number;
  value43: number;
  value44: number;
  value45: number;
  value46: number;
  value47: number;
  value48: number;
  value49: number;
  value50: number; // All POGS
  flag1: number; // Delete DBKey
  flag2: number; // ProtectionFlag
  flag3: number;
  flag4: number;
  flag5: number;
  flag6: number;
  flag7: number;
  flag8: number;
  flag9: number; // Web Post 1
  flag10: number; // Web Post 2
  fillPattern: number; // The fill pattern applied to the backboard, visible when view configured to display shading patterns.
  number: number; // The index number of the planogram within the project.
  segmentsToPrint: string; // The segments within the planogram to be printed, used with layouts to control how segments can be grouped together for printing.
  fileName: string; // The original filename for individual planogram files merged into a project. If the planogram(s) are re-saved as individual planogram projects (or InterCept files), the original file name can be restored.
  layoutFileName: string; // The layout file associated with the planogram for printing.
  notes: string; // Notes stored for the planogram.
  dbKey1: number; // Database keys specifying the planogram's location within the hierarchy.
  dbKey2: number; // The top parent node is stored within DBKey1, the highest child node in DBKey2, etc.
  dbKey3: number;
  dbKey4: number;
  dbKey5: number;
  dbKey6: number;
  dbKey7: number;
  dbKey8: number;
  dbKey9: number;
  dbKey10: number;
  movement: number; // The movement for the planogram.
  capacity: number; // The capacity for the planogram.
  numberOfProductsAllocated: number; // The number of products allocated on the planogram.
  salesAllocated: number; // The total sales for products allocated on the planogram.
  costAllocated: number; // The total cost for products allocated on the planogram.
  movementAllocated: number; // The total movement for products allocated on the planogram.
  marginAllocated: number; // The margin for products allocated on the planogram.
  annualProfitAllocated: number; // The annual profit for products allocated on the planogram.
  sourceFileType: number; // The original source for the planogram (i.e., Space Planning, InterCept, Pegman).
  status1: string; // The chosen status for the planogram: Field updated by Enforcer.
  status2: string; // The chosen status for the planogram.
  status3: string; // The chosen status for the planogram.
  dateCreated: Date; // The date the planogram was created. This date is not filled in automatically, and must be specified by the user.
  dateModified: Date; // The date the planogram was modified. This date is not filled in automatically, and must be specified by the user.
  datePending: Date; // The pending date for the planogram. This date is not filled in automatically, and must be specified by the user.
  dateEffective: Date; // The effective date for the planogram. This date is not filled in automatically, and must be specified by the user.
  dateFinished: Date; // The finished date for the planogram. This date is not filled in automatically, and must be specified by the user.
  date1: Date; // An additional date field for the planogram. This date is not filled in automatically, and must be specified by the user.
  date2: Date; // An additional date field for the planogram. This date is not filled in automatically, and must be specified by the user.
  date3: Date; // An additional date field for the planogram. This date is not filled in automatically, and must be specified by the user. Web Wiz Use Only
  createdBy: string; // The name of the person creating the planogram. This field is not filled in automatically, and must be entered by the user.
  modifiedBy: string; // The name of the person modifying the planogram. This field is not filled in automatically, and must be entered by the user.
  warningsCount: number; // The number of warnings associated with the planogram.
  floorBitmapId: string; // The image associated with the floor beneath the planogram. This image is visible only in 3D view
  doorTransparency: number; // The amount of transparency (from 0-90%) to apply to door frames on segments in 3D view
  floorTileWidth: number; // The width of the image to be tiled across the floor displayed in 3D view
  floorTileDepth: number; // The depth of the image to be tiled across the floor displayed in 3D view
  inventoryModelManual: number; // Specifies whether the Manual inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelCaseMultiple: number; // Specifies whether the Case Multiple inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelDaysSupply: number; // Specifies whether the Days Supply inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelPeak: number; // Specifies whether the Peak Demand inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelMaxUnits: number; // Specifies whether the Maximum Units inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelMinUnits: number; // Specifies whether the Minimum Units inventory model is being used in determining the target inventory for products on the planogram
  caseMultiple: number; // Specifies whether the Minimum Units inventory model is being used in determining the target inventory for products on the planogram
  daysSupply: number; // The minimum days of supply required for all products on the planogram when using the Days Supply inventory model to calculate the target inventory for products on the planogram
  demandCycleLength: number; // The number of days in the demand cycle for products on the planogram
  peakSafetyFactor: number; // The percentage of stock required for products on the planogram, including extra product to account for unusually high demand in a specific cycle
  backroomStock: number; // The default percentage of product held in reserve in the back room for products
  demand1: number; // The percentage of business done (or the proportional amount of business done) on each day in the demand cycle for the planogram
  demand2: number;
  demand3: number;
  demand4: number;
  demand5: number;
  demand6: number;
  demand7: number; // The percentage of business done (or the proportional amount of business done) on each day in the demand cycle for the planogram
  deliverySchedule: string; // The days in the demand cycle on which products are delivered for the planogram
  id: string; // The unique ID for the planogram
  department: string; // The department to which the planogram belongs
  combinedPerfIndexAllocated: number; // The average Combined Performance Index (CPI) for products allocated on the planogram
  roiiCostAllocated: number; // The ROII cost for the planogram, restricted to those products allocated on the planogram
  roiiRetailAllocated: number; // The ROII retail for the planogram, restricted to those products allocated on the planogram
  partId: string; // An additional field for storing an identifier for the planogram
  profit: number; // The estimated profit for all Performance records associated with the planogram, including Performance records for products not allocated on the planogram
  profitAllocated: number; // The estimated profit for all products on the planogram, restricted to those products allocated on the planogram
  capacityUnrestricted: number; // The number of units that would fit on fixture in the planogram if no other merchandising restrictions (such as Max High) were applied
  gln: string; // Global Location Number. An optional number used to identify a planogram, segment, or fixture.
  warning: string; // The warning associated with the object.
  warningNumber: number; // The number of the warning associated with the object
  customData: string; // A field that lets you store specific custom data for the selected planogram
  planogramGuid: string; // The system-generated unique identifier of the planogram, used when matching PEM planograms to master planograms
  dbGuid: string; // The system-generated unique identifier of the original database in which the planogram was saved
  abbrevName: string; // The abbreviated name for the planogram
  category: string; // The category to which the planogram belongs
  subcategory: string; // The subcategory to which the planogram belongs
  numberOfRecommendations: number; // The number of assortment recommendations that exist for the planogram.
  // source: string; // Not in Object Mapping Spreadsheet
  allocationGroup: string; // Used by Floorplan Space Allocation to determine which planograms are associated to one another
  allocationSequence: number; // Used by Floorplan Space Allocation to determine the order to place planograms which are associated to one another
  allocationTargetMin: number; // Used by Floorplan Space Allocation to determine the smallest length in inches of a planogram
  allocationTargetMax: number; // Used by Floorplan Space Allocation to determine the largest length in inches of a planogram
  canSegment: boolean; // Used by Floorplan Space Allocation to determine if a planogram can be segmented onto two different flooplan fixtures
  canSplit: number; // Used by Floorplan Space Allocation to determine the order to place planograms which are associated to one another
  pgStatus: number; // The current status of the planogram in the generation process.
  pgScorePercent: number; // The numeric score that denotes the success of the planogram generation process for the selected planogram. The score is calculated as the percentage of included facings (after the Initial phase) that remain on the planogram at the end of the Fill Out phase.
  pgScoreNote: string; // The description of the score assigned to the generated planogram. If an error occurs when generating a planogram, this field identifies the error.
  pgWarningsCount: number; // The number of warnings that occurred when generating the planogram.
  pgErrorsCount: number; // The number of warnings that occurred when generating the planogram.
  pgActionList: string; // The list of PG action rules used to generate the planogram
  pgMaxStageReduce: number; // The highest Stage number that is applied to the planogram during the Reduce to Fit phase of the generation process.
  pgmaxStageFillout: number; // The highest Stage number that is applied to the planogram during the Fill Out Space phase of the generation process.
  pgType: number; // Specifies whether the planogram is a PG template, PG auxilary template, or a generated planogram.
  modelFilename: string; // The 3DS filename associated with the planogram in Floor Planning
  dbfamilykey: number; // NOT in Object Mapping Spreadsheet
  dbreplacekey: number; // NOT in Object Mapping Spreadsheet
  dbversionKey: number; // The unique identifier corresponding to the Live version of the planogram within the version group.
  dbParentPgAuxTemplateKey: number; // The unique identifier of the auxilary template planogram used to generate this planogram.
  dbparentpgsourcekey: number; // NOT in Object Mapping Spreadsheet
  dbParentPgTemplateKey: number; // The unique identifier of the template planogram used to generate this planogram.
  dbPgTimeDone: Date; // The date and time at which the Planogram Generator planogram is created or was last created
  pgServerName: string; // The name of the server on which the Planogram Generator planogram is created
  pr_status: string; // NOT in Object Mapping Spreadsheet
  movement_period: string; // NOT in Object Mapping Spreadsheet; // NOT in Object Mapping Spreadsheet
  allocation_section_splits: string; // NOT in Object Mapping Spreadsheet
  allocation_priority: string; // NOT in Object Mapping Spreadsheet
  checksum_1: string; // NOT in Object Mapping Spreadsheet
  checksum_2: string; // NOT in Object Mapping Spreadsheet
  checksum_3: string; // NOT in Object Mapping Spreadsheet
  checksum_4: string; // NOT in Object Mapping Spreadsheet
  checksum_5: string; // NOT in Object Mapping Spreadsheet
  use_as_subplanogram: string; // NOT in Object Mapping Spreadsheet
  allocation_merge: string; // NOT in Object Mapping Spreadsheet
  is_merged: boolean; // NOT in Object Mapping Spreadsheet
  inventory_model_demand_mean_variance: string; // NOT in Object Mapping Spreadsheet
  service_level: string; // NOT in Object Mapping Spreadsheet
  pg_results_1: string; // NOT in Object Mapping Spreadsheet
  pg_results_2: string; // NOT in Object Mapping Spreadsheet
  pg_results_3: string; // NOT in Object Mapping Spreadsheet
  pg_results_4: string; // NOT in Object Mapping Spreadsheet
  pg_results_5: string; // NOT in Object Mapping Spreadsheet

  // NOTE: in Object Mapping these fields are redundant (written twice)
  // sales
  // cost
  // margin
  // capacity_cost
  // capacity_retail
  // annual_profit
  // roii_cost
  // roii_retail
  // movement
  // capacity
  // number_of_products_allocated
  // sales_allocated
  // cost_allocated
  // movement_allocated
  // margin_allocated
  // annual_profit_allocated
  // number_of_fixtures
  // number_of_segments
  // number_of_drawings
  // warnings_count
  // combined_performance_index
  // profit
  // profit_allocated
  // capacity_unrestricted
  // warning
  // warning_number
  // number_of_recommendations

  // NOTE: These fields are written near the redundant fields above but not found in Object Mapping Spreadsheet
  // combined_performance_index_allocated: string; // NOT in Object Mapping Spreadsheet
  // roii_cost_allocated: string; // NOT in Object Mapping Spreadsheet
  // roii_retail_allocated: string; // NOT in Object Mapping Spreadsheet
  // movement_period_used: string; // NOT in Object Mapping Spreadsheet
  // number_of_subplanogram_spaces: string; // NOT in Object Mapping Spreadsheet

  // Below are fields that available in Object Mapping but not in PSA Header Spreadsheet
  dbTime: Date; // The date and time at which the planogram was saved to the database.
  dbUser: string; // The user who saved the planogram to the database.
  dbCurrentUser: string; // The current user who has opened the planogram from the database.
  dbStatus: number; // The current status for the planogram.
  dbDateEffectiveFrom: Date; // The date from which the planogram will be active/Live.
  originalFilename: string; // The original filename of the planogram.
  originalName: string; // The original name of the planogram.
  originalNumber: number; // The index number of the planogram within the Space Planning project.
  dbDateEffectiveTo: Date; // The date after which the planogram becomes inactive/Historic.
  authorityLevel: number; // Specifies whether a planogram is authorized or unauthorized for use
  performanceUpdateStatus: string;
  textureOverride: string; // The texture image associated with the planogram in Floor Planning
  numberOfSections: number; // The number of sections that exist for the planogram in the selected floorplan
  linear: number; // The linear space available on the planogram
  square: number; // The square space available on the planogram
  cubic: number; // The cubic space available on the planogram
  floorplans: number; // The number of floorplans in the current project on which the planogram is merchandised
  floorplanAlias: string; // The floorplan identifier, used for updating Performance level data from a planogram library
  sourceProjectFilename: string; // The original Space Planning project name of the project containing the planogram
  sourceProjectPlanogram: string; // The original planogram name for the planogram in the Space Planning project
  uom: string; // The unit of measure for the planogram
  pogIndex: number; // The index number for the planogram in the Space Planning project
  pendingDate: Date; // The Pending date for the planogram in Floor Planning
  liveDate: Date; // The Live date for the planogram in Floor Planning
  finishedDate: Date; // The Finished date for the planogram in Floor Planning
  tileTextures: number; // Specifies whether to tile textures across the planogram in Floor Planning
  textureAllFaces: number; // Specifies whether to display textures on all planogram sides in Floor Planning
  transparency: number; // The amount of transparency (1-10) to apply to the planogram in Floor Planning
}

export const planogramRecordValidation = {
  name: "VARCHAR", // The name of the planogram.
  dbKey: "BIGINT", // The unique identifier for the planogram. "key" in PSA Headers Speadsheet.
  width: "NUMERIC", // The width of the planogram.
  height: "NUMERIC", // The height of the planogram.
  depth: "NUMERIC", // The depth of the planogram.
  color: "INT", // The color of the planogram.
  // changed: "INT", // Specifies whether the planogram data has changed during the current Space Planning or Floor Planning session.
  backDepth: "NUMERIC", // Depth of the backboard
  drawBack: "INT", // Indicates whether or not the back is drawn
  baseWidth: "NUMERIC", // The width of the base
  baseHeight: "NUMERIC", // The height of the base
  baseDepth: "NUMERIC", // The depth of the base
  drawBase: "INT", // Indicates whether or not the base is drawn.
  baseColor: "INT", // Indicates whether or not the base is drawn.
  drawNotches: "INT", // Indicates whether or not the notches are drawn on the planogram.
  notchOffset: "NUMERIC", // The distance from the floor from which to start displaying notches.
  notchSpacing: "NUMERIC", // Indicates the vertical spacing of the notches on the notch bars.
  doubleNotches: "INT", // Indicates whether or not the notches are double.
  notchColor: "INT", // The color of the notches on the planogram expressed in RGB integer.
  notchMarks: "INT", // Indicates whether or not the notch bars are to be drawn along the sides of the planogram’s segments.
  drawPegs: "INT", // Indicates whether or not the pegs are drawn on the pegboard.
  drawPegholes: "INT", // Indicates whether or not the pegholes are drawn on the pegboard.
  trafficFlow: "INT", // The merchandise direction on the planogram
  autoCreated: "INT", // The merchandise direction on the planogram
  shapeId: "VARCHAR", // The shape ID associated with the planogram.
  bitmapId: "VARCHAR", // The image file associated with the planogram.
  merchXMin: "INT", // Minimum number of horizontal facings of child positions. On the Planogram Properties, this is labeled “Minimum facings”.
  merchXMax: "INT", // Maximum number of horizontal facings of child positions, includes horizontal caps. On the Planogram Properties, this is labeled “Maximum facings”.
  merchXUprights: "INT", // The maximum number of normal upright units horizontally, used to specify the target number of upright facings in conjunction with horizontal capping (MerchYCaps). This will be constrained by MerchXMax or the available merchandising space of the fixture.
  merchXCaps: "INT", // Maximum vertical number of caps, will be constrained by MerchYMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps High”.
  merchXPlacement: "INT", // Specifies how the positions are to be placed horizontally. On the Planogram Properties, this is labeled “Placement left/right” (options are Manually placed, At the edge, Stacked against others, Spread evenly).
  merchXNumber: "INT", // Specifies how the number of horizontal units of child positions is controlled. On the Planogram Properties, this is labeled “Number of facings wide” (options are Manually determined, Only 1, Fill available space).
  merchXSize: "INT", // Specifies how the number of horizontal units of child positions is controlled. On the Planogram Properties, this is labeled “Number of facings wide” (options are Manually determined, Only 1, Fill available space).
  merchXDirection: "INT", // Specifies whether or not to base merchandising from right instead of left. On the Planogram Properties, this is labeled “Starting from”.
  merchXSqueeze: "INT", // Determines whether or not individual facing can be squeezed horizontal. On the Planogram Properties, this is labeled “Facings can be squeezed”.
  merchYMin: "INT", // Minimum number of vertical facings of child positions. On the Planogram Properties, this is labeled “Minimum units high”.
  merchYMax: "INT", // Maximum number of vertical facings of child positions, includes vertical caps. On the Planogram Properties, this is labeled “Maximum units high”.
  merchYUprights: "INT", // Maximum number of vertical facings of child positions, includes vertical caps. On the Planogram Properties, this is labeled “Maximum units high”.
  merchYCaps: "INT", // Maximum vertical number of caps, will be constrained by MerchYMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps High”.
  merchYPlacement: "INT", // Maximum vertical number of caps, will be constrained by MerchYMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps High”.
  merchYNumber: "INT", // Specifies how the number of vertical units of child positions is controlled. On the Planogram Properties, this is labeled “Number of units high” (options are Manually determined, Only 1, Fill available space).
  merchYSize: "INT", // Indicates how the height of the units within a position is to be controlled. On the Planogram Properties, this is labeled “Height of units” (options are Normal, Expand/Squeeze facings to fill space, Space out facings to fill space).
  merchYDirection: "INT", // Specifies whether or not to base merchandising from top instead of base. On the Planogram Properties, this is labeled “Starting from”.
  merchYSqueeze: "INT", // Determines whether or not individual facing can be squeezed vertically. On the Planogram Properties, this is labeled “Facings can be squeezed”.
  merchZMin: "INT", // Minimum number of units deep of child positions. On the Planogram Properties, this is labeled “Minimum units deep”.
  merchZMax: "INT", // Maximum number of units deep of child positions, includes depth caps. On the Planogram Properties, this is labeled “Maximum units deep”.
  merchZUprights: "INT", // Maximum number of units deep of child positions, includes depth caps. On the Planogram Properties, this is labeled “Maximum units deep”.
  merchZCaps: "INT", // Maximum number of caps deep, will be constrained by MerchZMax setting or the available merchandising space of the fixture. On the Planogram Properties, this is labeled “Target Caps Deep”.
  merchZPlacement: "INT", // Specifies how the positions are to be placed depth wise. On the Planogram Properties, this is labeled “Placement back/front” (options are Manually placed, At the edge, Stacked against others, Spread evenly).
  merchZNumber: "INT", // Specifies how the number of units deep of child positions is controlled. On the Planogram Properties, this is labeled “Number of units deep” (options are Manually determined, Only 1, Fill available space).
  merchZSize: "INT", // Indicates how the depth of the units within a position is to be controlled. On the Planogram Properties, this is labeled “Depth of units” (options are Normal, Expand/Squeeze facings to fill space, Space out facings to fill space).
  merchZDirection: "INT", // Specifies whether or not to base merchandising from back instead of front. On the Planogram Properties, this is labeled “Starting from”.
  merchZSqueeze: "INT", // Determines whether or not individual facing can be squeezed depth wise. On the Planogram Properties, this is labeled “Facings can be squeezed”.
  // numberOfFixtures: "INT", // The number of fixtures contained within the planogram
  // numberOfSegments: "INT", // The number of fixtures contained within the planogram
  // numberOfDrawings: "INT", // The number of segments contained within the planogram
  combinedPerformanceIndex: "NUMERIC", // The number of drawn objects on the planogram view
  numberOfStores: "INT", // The number of stores that are represented by the planogram.
  // sales: "NUMERIC", // Total sales for the planogram
  // cost: "NUMERIC", // The total cost for all positions on the planogram.
  // margin: "NUMERIC", // The total margin for the planogram.
  // capacityCost: "NUMERIC", // The total capacity cost for all the positions on the planogram.
  // capacityRetail: "NUMERIC", // The total capacity retail for all the positions on the planogram.
  // annualProfit: "NUMERIC", // Annual profit for the planogram.
  // roiiCost: "NUMERIC", // The ROII cost for the planogram.
  // roiiRetail: "NUMERIC", // The ROII retail for the planogram.
  notchWidth: "NUMERIC", // The width of the notch bar.
  desc1: "VARCHAR", // ProtectionDesc: Updated PMD to say "ProtectionDesc" instead of "PlanogramType" 2021.  This field is used to identify the store's ProtectionDesc and should be updating from the store table.
  desc2: "VARCHAR", // Group/Base: Field updated by Enforcer.
  desc3: "VARCHAR", // City: Field updated by Enforcer.
  desc4: "VARCHAR", // City Store #: Field updated by Enforcer.
  desc5: "VARCHAR", // Dept #: Field updated by Enforcer.
  desc6: "VARCHAR", // Dept Name: Field updated by Enforcer.
  desc7: "VARCHAR", // Sub Dept #: Field updated by Enforcer.
  desc8: "VARCHAR", // Sub Dept Name: Field updated by Enforcer.
  desc9: "VARCHAR", // Section: Field updated by Enforcer.
  desc10: "VARCHAR", // Section Size: Field updated by Enforcer.
  desc11: "VARCHAR", // Cluster #: Field updated by Enforcer.
  desc12: "VARCHAR", // Store Cluster: Field updated by Enforcer. Not being updated?  Fresh using 2021 (segmentation)
  desc13: "VARCHAR", // Primary Section Cluster: Field updated by Enforcer.
  desc14: "VARCHAR", // Primary Section
  desc15: "VARCHAR", // POG Type: Field updated by Enforcer.
  desc16: "VARCHAR", // Base/Group POG Name: Field updated by Enforcer.
  desc17: "VARCHAR", // Obstruction: Field updated by Enforcer.
  desc18: "VARCHAR", // Base Shelf Depth
  desc19: "VARCHAR", // Upper Shelf Depth
  desc20: "VARCHAR",
  desc21: "VARCHAR",
  desc22: "VARCHAR", // Profile Type: Values will be high (>61) or low (<61)
  desc23: "VARCHAR", // Other: Field updated by Enforcer.
  desc24: "VARCHAR", // Revision Type: Field updated by Enforcer.
  desc25: "VARCHAR", // Tailored Plan
  desc26: "VARCHAR", // BDM: Field updated in CAS_SPC_PLAN_UPD_PKG for POGs in status 1,2,3,4.  BDM is pulled from cas.heb_us_section.
  desc27: "VARCHAR", // Region: Field updated by Enforcer?  Not being updated. Fresh using 2021.
  desc28: "VARCHAR", // District: Fresh using 2021 (distribution limitations)
  desc29: "VARCHAR", // Keywords: Field updated by Enforcer.
  desc30: "VARCHAR", // Creator
  desc31: "VARCHAR", // Unique
  desc32: "VARCHAR", // Cluster Desc: Fresh using 2021 (assortment attributes)
  desc33: "VARCHAR", // Fixture Type: Fresh using 2021 for case type.
  desc34: "VARCHAR", // Tailored Date
  desc35: "VARCHAR", // Beer Date Info
  desc36: "VARCHAR",
  desc37: "VARCHAR",
  desc38: "VARCHAR",
  desc39: "VARCHAR",
  desc40: "VARCHAR",
  desc41: "VARCHAR",
  desc42: "VARCHAR",
  desc43: "VARCHAR",
  desc44: "VARCHAR",
  desc45: "VARCHAR", // CKS User
  desc46: "VARCHAR",
  desc47: "VARCHAR", // MM
  desc48: "VARCHAR", // Store Assign
  desc49: "VARCHAR", // Store Assign(Cont)
  desc50: "VARCHAR", // All POGs
  value1: "NUMERIC", // ProtectionOrder
  value2: "NUMERIC",
  value3: "NUMERIC",
  value4: "NUMERIC",
  value5: "NUMERIC",
  value6: "NUMERIC",
  value7: "NUMERIC",
  value8: "NUMERIC",
  value9: "NUMERIC",
  value10: "NUMERIC",
  value11: "NUMERIC",
  value12: "NUMERIC",
  value13: "NUMERIC",
  value14: "NUMERIC",
  value15: "NUMERIC",
  value16: "NUMERIC",
  value17: "NUMERIC",
  value18: "NUMERIC",
  value19: "NUMERIC",
  value20: "NUMERIC",
  value21: "NUMERIC",
  value22: "NUMERIC",
  value23: "NUMERIC",
  value24: "NUMERIC",
  value25: "NUMERIC",
  value26: "NUMERIC",
  value27: "NUMERIC",
  value28: "NUMERIC",
  value29: "NUMERIC",
  value30: "NUMERIC", // Archive: If value = 9999 the pog will be deleted from ikb
  value31: "NUMERIC",
  value32: "NUMERIC",
  value33: "NUMERIC",
  value34: "NUMERIC",
  value35: "NUMERIC", // Shelf Life
  value36: "NUMERIC",
  value37: "NUMERIC",
  value38: "NUMERIC",
  value39: "NUMERIC",
  value40: "NUMERIC", // Value = 9999 the pog was created by Create Versioning Proscript; Value = 9998 the pog was created by RG1
  value41: "NUMERIC",
  value42: "NUMERIC",
  value43: "NUMERIC",
  value44: "NUMERIC",
  value45: "NUMERIC",
  value46: "NUMERIC",
  value47: "NUMERIC",
  value48: "NUMERIC",
  value49: "NUMERIC",
  value50: "NUMERIC", // All POGS
  flag1: "INT", // Delete DBKey
  flag2: "INT", // ProtectionFlag
  flag3: "INT",
  flag4: "INT",
  flag5: "INT",
  flag6: "INT",
  flag7: "INT",
  flag8: "INT",
  flag9: "INT", // Web Post 1
  flag10: "INT", // Web Post 2
  fillPattern: "INT", // The fill pattern applied to the backboard, visible when view configured to display shading patterns.
  // number: "INT", // The index number of the planogram within the project.
  segmentsToPrint: "VARCHAR", // The segments within the planogram to be printed, used with layouts to control how segments can be grouped together for printing.
  fileName: "VARCHAR", // The original filename for individual planogram files merged into a project. If the planogram(s) are re-saved as individual planogram projects (or InterCept files), the original file name can be restored.
  layoutFileName: "VARCHAR", // The layout file associated with the planogram for printing.
  notes: "VARCHAR", // Notes stored for the planogram.
  dbKey1: "NUMERIC", // Database keys specifying the planogram's location within the hierarchy.
  dbKey2: "NUMERIC", // The top parent node is stored within DBKey1, the highest child node in DBKey2, etc.
  dbKey3: "NUMERIC",
  dbKey4: "NUMERIC",
  dbKey5: "NUMERIC",
  dbKey6: "NUMERIC",
  dbKey7: "NUMERIC",
  dbKey8: "NUMERIC",
  dbKey9: "NUMERIC",
  dbKey10: "NUMERIC",
  // movement: "NUMERIC", // The movement for the planogram.
  // capacity: "INT", // The capacity for the planogram.
  // numberOfProductsAllocated: "INT", // The number of products allocated on the planogram.
  // salesAllocated: "NUMERIC", // The total sales for products allocated on the planogram.
  // costAllocated: "NUMERIC", // The total cost for products allocated on the planogram.
  // movementAllocated: "NUMERIC", // The total movement for products allocated on the planogram.
  // marginAllocated: "NUMERIC", // The margin for products allocated on the planogram.
  // annualProfitAllocated: "NUMERIC", // The annual profit for products allocated on the planogram.
  sourceFileType: "INT", // The original source for the planogram (i.e., Space Planning, InterCept, Pegman).
  status1: "VARCHAR", // The chosen status for the planogram: Field updated by Enforcer.
  status2: "VARCHAR", // The chosen status for the planogram.
  status3: "VARCHAR", // The chosen status for the planogram.
  dateCreated: "DATE", // The date the planogram was created. This date is not filled in automatically, and must be specified by the user.
  dateModified: "DATE", // The date the planogram was modified. This date is not filled in automatically, and must be specified by the user.
  datePending: "DATE", // The pending date for the planogram. This date is not filled in automatically, and must be specified by the user.
  dateEffective: "DATE", // The effective date for the planogram. This date is not filled in automatically, and must be specified by the user.
  dateFinished: "DATE", // The finished date for the planogram. This date is not filled in automatically, and must be specified by the user.
  date1: "DATE", // An additional date field for the planogram. This date is not filled in automatically, and must be specified by the user.
  date2: "DATE", // An additional date field for the planogram. This date is not filled in automatically, and must be specified by the user.
  date3: "DATE", // An additional date field for the planogram. This date is not filled in automatically, and must be specified by the user. Web Wiz Use Only
  createdBy: "VARCHAR", // The name of the person creating the planogram. This field is not filled in automatically, and must be entered by the user.
  modifiedBy: "VARCHAR", // The name of the person modifying the planogram. This field is not filled in automatically, and must be entered by the user.
  // warningsCount: "INT", // The number of warnings associated with the planogram.
  // dbDateEffectiveFrom: "DATE", // TODO existing DB column but commented out as this header is not found in PSA file content
  // originalFilename: "VARCHAR"; // TODO existing DB column but commented out as this header is not found in PSA file content
  // originalName: "VARCHAR"; // TODO existing DB column but commented out as this header is not found in PSA file content
  // originalNumber: "NUMERIC"; // TODO existing DB column but commented out as this header is not found in PSA file content
  // dbDateEffectiveTo: "DATE", // TODO existing DB column but commented out as this header is not found in PSA file content
  floorBitmapId: "VARCHAR", // The image associated with the floor beneath the planogram. This image is visible only in 3D view
  doorTransparency: "NUMERIC", // The amount of transparency (from 0-90%) to apply to door frames on segments in 3D view
  floorTileWidth: "NUMERIC", // The width of the image to be tiled across the floor displayed in 3D view
  floorTileDepth: "NUMERIC", // The depth of the image to be tiled across the floor displayed in 3D view
  inventoryModelManual: "INT", // Specifies whether the Manual inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelCaseMultiple: "INT", // Specifies whether the Case Multiple inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelDaysSupply: "INT", // Specifies whether the Days Supply inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelPeak: "INT", // Specifies whether the Peak Demand inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelMaxUnits: "INT", // Specifies whether the Maximum Units inventory model is being used in determining the target inventory for products on the planogram
  inventoryModelMinUnits: "INT", // Specifies whether the Minimum Units inventory model is being used in determining the target inventory for products on the planogram
  caseMultiple: "NUMERIC", // Specifies whether the Minimum Units inventory model is being used in determining the target inventory for products on the planogram
  daysSupply: "NUMERIC", // The minimum days of supply required for all products on the planogram when using the Days Supply inventory model to calculate the target inventory for products on the planogram
  demandCycleLength: "INT", // The number of days in the demand cycle for products on the planogram
  peakSafetyFactor: "NUMERIC", // The percentage of stock required for products on the planogram, including extra product to account for unusually high demand in a specific cycle
  backroomStock: "NUMERIC", // The default percentage of product held in reserve in the back room for products
  demand1: "NUMERIC", // The percentage of business done (or the proportional amount of business done) on each day in the demand cycle for the planogram
  demand2: "NUMERIC",
  demand3: "NUMERIC",
  demand4: "NUMERIC",
  demand5: "NUMERIC",
  demand6: "NUMERIC",
  demand7: "NUMERIC", // The percentage of business done (or the proportional amount of business done) on each day in the demand cycle for the planogram
  deliverySchedule: "VARCHAR", // The days in the demand cycle on which products are delivered for the planogram
  id: "VARCHAR", // The unique ID for the planogram
  department: "VARCHAR", // The department to which the planogram belongs
  // combinedPerfIndexAllocated: "NUMERIC", // The average Combined Performance Index (CPI) for products allocated on the planogram
  // roiiCostAllocated: "NUMERIC", // The ROII cost for the planogram, restricted to those products allocated on the planogram
  // roiiRetailAllocated: "NUMERIC", // The ROII retail for the planogram, restricted to those products allocated on the planogram
  partId: "VARCHAR", // An additional field for storing an identifier for the planogram
  // profit: "NUMERIC", // The estimated profit for all Performance records associated with the planogram, including Performance records for products not allocated on the planogram
  // profitAllocated: "NUMERIC", // The estimated profit for all products on the planogram, restricted to those products allocated on the planogram
  // capacityUnrestricted: "INT", // The number of units that would fit on fixture in the planogram if no other merchandising restrictions (such as Max High) were applied
  gln: "VARCHAR", // Global Location Number. An optional number used to identify a planogram, segment, or fixture.
  // warning: "VARCHAR", // The warning associated with the object.
  // warningNumber: "INT", // The number of the warning associated with the object
  customData: "VARCHAR", // A field that lets you store specific custom data for the selected planogram
  planogramGuid: "VARCHAR", // The system-generated unique identifier of the planogram, used when matching PEM planograms to master planograms
  dbGuid: "VARCHAR", // The system-generated unique identifier of the original database in which the planogram was saved
  abbrevName: "VARCHAR", // The abbreviated name for the planogram
  category: "VARCHAR", // The category to which the planogram belongs
  subcategory: "VARCHAR", // The subcategory to which the planogram belongs
  // numberOfRecommendations: "INT", // The number of assortment recommendations that exist for the planogram.
  // numberOfSections: "INT", // TODO existing DB column but commented out as this header is not found in PSA file content
  // linear: "NUMERIC", // TODO existing DB column but commented out as this header is not found in PSA file content
  // square: "NUMERIC", // TODO existing DB column but commented out as this header is not found in PSA file content
  // cubic: "NUMERIC", // TODO existing DB column but commented out as this header is not found in PSA file content
  source: "INT", // TODO existing DB column but commented out as this header is not found in PSA file content
  allocationGroup: "VARCHAR", // Used by Floorplan Space Allocation to determine which planograms are associated to one another
  allocationSequence: "INT", // Used by Floorplan Space Allocation to determine the order to place planograms which are associated to one another
  allocationTargetMin: "NUMERIC", // Used by Floorplan Space Allocation to determine the smallest length in inches of a planogram
  allocationTargetMax: "NUMERIC", // Used by Floorplan Space Allocation to determine the largest length in inches of a planogram
  canSegment: "BOOLEAN", // Used by Floorplan Space Allocation to determine if a planogram can be segmented onto two different flooplan fixtures
  canSplit: "INT", // TODO INT4 CHECK (can_split IN (-1, 0, 1, 2)),
  pgStatus: "INT", // The current status of the planogram in the generation process.
  pgScorePercent: "INT", // The numeric score that denotes the success of the planogram generation process for the selected planogram. The score is calculated as the percentage of included facings (after the Initial phase) that remain on the planogram at the end of the Fill Out phase.
  pgScoreNote: "VARCHAR", // The description of the score assigned to the generated planogram. If an error occurs when generating a planogram, this field identifies the error.
  pgWarningsCount: "INT", // The number of warnings that occurred when generating the planogram.
  pgErrorsCount: "INT", // The number of warnings that occurred when generating the planogram.
  pgActionList: "TEXT", // The list of PG action rules used to generate the planogram
  pgMaxStageReduce: "NUMERIC", // The highest Stage number that is applied to the planogram during the Reduce to Fit phase of the generation process.
  pgmaxStageFillout: "NUMERIC", // The highest Stage number that is applied to the planogram during the Fill Out Space phase of the generation process.
  pgType: "INT", // Specifies whether the planogram is a PG template, PG auxilary template, or a generated planogram.
  modelFilename: "VARCHAR", // The 3DS filename associated with the planogram in Floor Planning
  dbfamilykey: "NUMERIC", // NOT in Object Mapping Spreadsheet
  dbreplacekey: "NUMERIC", // NOT in Object Mapping Spreadsheet
  dbversionKey: "INT", // The unique identifier corresponding to the Live version of the planogram within the version group.
  dbParentPgAuxTemplateKey: "INT", // The unique identifier of the auxilary template planogram used to generate this planogram.
  dbparentpgsourcekey: "NUMERIC", // NOT in Object Mapping Spreadsheet
  dbParentPgTemplateKey: "INT", // The unique identifier of the template planogram used to generate this planogram.
  dbPgTimeDone: "DATE", // The date and time at which the Planogram Generator planogram is created or was last created
  pgServerName: "VARCHAR", // The name of the server on which the Planogram Generator planogram is created
  pr_status: "VARCHAR", // NOT in Object Mapping Spreadsheet
  movement_period: "VARCHAR", // NOT in Object Mapping Spreadsheet; // NOT in Object Mapping Spreadsheet
  allocation_section_splits: "VARCHAR", // NOT in Object Mapping Spreadsheet
  allocation_priority: "VARCHAR", // NOT in Object Mapping Spreadsheet
  checksum_1: "VARCHAR", // NOT in Object Mapping Spreadsheet
  checksum_2: "VARCHAR", // NOT in Object Mapping Spreadsheet
  checksum_3: "VARCHAR", // NOT in Object Mapping Spreadsheet
  checksum_4: "VARCHAR", // NOT in Object Mapping Spreadsheet
  checksum_5: "VARCHAR", // NOT in Object Mapping Spreadsheet
  use_as_subplanogram: "VARCHAR", // NOT in Object Mapping Spreadsheet
  allocation_merge: "VARCHAR", // NOT in Object Mapping Spreadsheet
  is_merged: "BOOLEAN", // NOT in Object Mapping Spreadsheet
  inventory_model_demand_mean_variance: "VARCHAR", // NOT in Object Mapping Spreadsheet
  service_level: "VARCHAR", // NOT in Object Mapping Spreadsheet
  pg_results_1: "VARCHAR", // NOT in Object Mapping Spreadsheet
  pg_results_2: "VARCHAR", // NOT in Object Mapping Spreadsheet
  pg_results_3: "VARCHAR", // NOT in Object Mapping Spreadsheet
  pg_results_4: "VARCHAR", // NOT in Object Mapping Spreadsheet
  pg_results_5: "VARCHAR", // NOT in Object Mapping Spreadsheet

  // NOTE: in Object Mapping these fields are redundant (written twice)
  // sales
  // cost
  // margin
  // capacity_cost
  // capacity_retail
  // annual_profit
  // roii_cost
  // roii_retail
  // movement
  // capacity
  // number_of_products_allocated
  // sales_allocated
  // cost_allocated
  // movement_allocated
  // margin_allocated
  // annual_profit_allocated
  // number_of_fixtures
  // number_of_segments
  // number_of_drawings
  // warnings_count
  // combined_performance_index
  // profit
  // profit_allocated
  // capacity_unrestricted
  // warning
  // warning_number
  // number_of_recommendations

  // NOTE: These fields are written near the redundant fields above but not found in Object Mapping Spreadsheet
  // combined_performance_index_allocated: "VARCHAR", // NOT in Object Mapping Spreadsheet
  // roii_cost_allocated: "VARCHAR", // NOT in Object Mapping Spreadsheet
  // roii_retail_allocated: "VARCHAR", // NOT in Object Mapping Spreadsheet
  // movement_period_used: "VARCHAR", // NOT in Object Mapping Spreadsheet
  // number_of_subplanogram_spaces: "VARCHAR", // NOT in Object Mapping Spreadsheet
};
